import { DialogForm, Label, useToast } from '@ais-ui/ui-core';
import { Job, useAbortJob } from '@ais-ui/websockets';
import React, { useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface AbortReportProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
  job: Job;
  onSuccess?: () => void;
}

export const AbortReport = ({
  showDialog,
  setShowDialog,
  job,
  onSuccess,
}: AbortReportProps) => {
  const { messages } = useLocalization();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { mutate } = useAbortJob();
  const { toast } = useToast();

  const onAbort = () => {
    setIsSubmitting(true);
    const reportName = job.name;

    if (!job.uuid) {
      toast({
        className: 'abort-report-error-toast',
        title: messages.genericToastTitleError,
        description: `Could not abort report "${reportName}": missing job ID`,
        variant: 'destructive',
      });
      setIsSubmitting(false);
      setShowDialog(false);
      return;
    }

    mutate(job.uuid, {
      onSuccess: () => {
        toast({
          className: 'abort-report-success-toast',
          title: messages.genericToastTitleSuccess,
          description: `Report "${reportName}" has been aborted`,
        });
        setIsSubmitting(false);
        setShowDialog(false);
        onSuccess?.();
      },
      onError: (error: any) => {
        markErrorHandledLocally(error);
        if (error.response?.status === 403) {
          toast({
            className: 'abort-report-error-toast',
            title: messages.genericToastTitleForbidden,
            description: `You do not have permission to abort report "${reportName}"`,
            variant: 'destructive',
          });
        } else {
          toast({
            className: 'abort-report-error-toast',
            title: messages.genericToastTitleError,
            description: `Failed to abort report "${reportName}"`,
            variant: 'destructive',
          });
        }
        setIsSubmitting(false);
        setShowDialog(false);
      },
    });
  };

  return (
    <DialogForm showDialog={showDialog} onOpenChange={setShowDialog} size="sm">
      <DialogForm.Header>
        <DialogForm.Title>Abort Report</DialogForm.Title>
        <DialogForm.Description>
          Are you sure you want to abort this report? This will stop the report
          processing.
        </DialogForm.Description>
      </DialogForm.Header>
      <DialogForm.Body>
        <div className="grid grid-cols-[auto_minmax(50%,_1fr)] gap-y-4 gap-x-2 items-baseline">
          <Label className="font-light">Report name</Label>
          <Label htmlFor="report-name">{job.name}</Label>
        </div>
      </DialogForm.Body>
      <DialogForm.Footer>
        <DialogForm.Cancel disabled={isSubmitting}>
          {messages.genericCancel}
        </DialogForm.Cancel>
        <DialogForm.Submit
          variant="destructive"
          id="report-confirm-abort-button"
          disabled={isSubmitting}
          onSubmit={onAbort}
        >
          {isSubmitting ? messages.genericSubmitting : 'Abort'}
        </DialogForm.Submit>
      </DialogForm.Footer>
    </DialogForm>
  );
};
